import React, { useEffect, useMemo, useState } from 'react';
import {
    getVentesMensuelles, updateVenteMensuelle, syncVentesFromActivites,
    getSuiviParCommercial, getSuiviParZone, getSuiviParCategorie,
    getCalendrierVentes, updateCalendrierVentes,
    getRepartitionObjectifs, updateRepartitionObjectifs,
    applyRepartitionObjectifs, updateObjectifClient,
    getZones, getCommerciaux
} from '../services/api';
import {
    FiCalendar, FiTrendingUp, FiTrendingDown, FiTarget, FiPackage,
    FiUsers, FiMapPin, FiTag, FiRefreshCw, FiEdit2, FiCheck, FiX,
    FiSettings, FiBarChart2, FiDownload, FiFilter, FiSearch
} from 'react-icons/fi';
import SearchableSelect from '../components/SearchableSelect';

const MOIS_LABELS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const CATEGORIES = [
    { val: 'challenger',     label: 'À challenger', color: '#F59E0B' },
    { val: 'cgv',            label: 'Sous CGV',     color: '#3B82F6' },
    { val: 'developper',     label: 'À développer', color: '#8B5CF6' },
    { val: 'grands_comptes', label: 'Grands comptes', color: '#16A34A' },
    { val: 'divers',         label: 'Divers',       color: '#94A3B8' }
];

const fmt = (n: any) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(Number(n) || 0);
const fmtPct = (n: any) => `${(Number(n) || 0).toFixed(1)}%`;

const SuiviMensuel: React.FC = () => {
    const now = new Date();
    const [annee, setAnnee] = useState(now.getFullYear());
    const [mois, setMois] = useState(now.getMonth() + 1);
    const [view, setView] = useState<'clients' | 'commerciaux' | 'zones' | 'categories'>('clients');

    const [rows, setRows] = useState<any[]>([]);
    const [totals, setTotals] = useState<any>({});
    const [calendrier, setCalendrier] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState('');
    const [filterZone, setFilterZone] = useState('');
    const [filterCategorie, setFilterCategorie] = useState('');
    const [filterCommercial, setFilterCommercial] = useState('');

    const [zones, setZones] = useState<any[]>([]);
    const [commerciaux, setCommerciaux] = useState<any[]>([]);

    const [editingCell, setEditingCell] = useState<string>(''); // "client_id" pour la vente, "obj-client_id" pour l'objectif
    const [editValue, setEditValue] = useState('');

    const [showSettings, setShowSettings] = useState(false);
    const [repartition, setRepartition] = useState<any>(null);

    const load = () => {
        setLoading(true);
        const params: any = { annee };
        if (mois) params.mois = mois;
        if (search) params.search = search;
        if (filterZone) params.zone_id = filterZone;
        if (filterCategorie) params.categorie = filterCategorie;
        if (filterCommercial) params.commercial_id = filterCommercial;

        const promise = view === 'commerciaux' ? getSuiviParCommercial(params)
                      : view === 'zones'       ? getSuiviParZone(params)
                      : view === 'categories'  ? getSuiviParCategorie(params)
                      : getVentesMensuelles(params);

        promise.then(res => {
            setRows(res.data.data || []);
            setTotals(res.data.totals || {});
            setCalendrier(res.data.calendrier || null);
        }).catch(e => console.error('Load error:', e))
          .finally(() => setLoading(false));
    };

    useEffect(() => {
        Promise.all([getZones(), getCommerciaux(), getRepartitionObjectifs(annee)])
            .then(([z, c, r]) => {
                setZones(z.data || []);
                setCommerciaux(c.data || []);
                setRepartition(r.data);
            });
    }, [annee]);

    useEffect(() => { load(); }, [annee, mois, view, search, filterZone, filterCategorie, filterCommercial]);

    const handleSaveVente = async (client_id: number, qty: string) => {
        try {
            await updateVenteMensuelle(client_id, annee, mois, { quantite: parseFloat(qty) || 0 });
            setEditingCell('');
            load();
        } catch (e: any) { alert(e.response?.data?.error || 'Erreur'); }
    };

    const handleSaveObjectif = async (client_id: number, qty: string) => {
        try {
            const objAnnuel = parseFloat(qty) || 0;
            await updateObjectifClient(client_id, annee, { objectif_annuel: objAnnuel });
            await applyRepartitionObjectifs({ client_ids: [client_id], annee });
            setEditingCell('');
            load();
        } catch (e: any) { alert(e.response?.data?.error || 'Erreur'); }
    };

    const handleSync = async () => {
        if (!window.confirm('Resynchroniser les ventes depuis les activités mobile pour ce mois ?')) return;
        try {
            const res = await syncVentesFromActivites({ annee, mois });
            alert(res.data.message);
            load();
        } catch (e: any) { alert(e.response?.data?.error || 'Erreur'); }
    };

    const handleApplyAll = async () => {
        if (!window.confirm(`Appliquer la répartition saisonnière ${annee} à tous les clients ?`)) return;
        try {
            const res = await applyRepartitionObjectifs({ all: true, annee });
            alert(res.data.message);
            load();
        } catch (e: any) { alert(e.response?.data?.error || 'Erreur'); }
    };

    const yearsOptions = [2024, 2025, 2026, 2027];

    const categorieColor = (cat: string) => CATEGORIES.find(c => c.val === cat)?.color || '#94A3B8';
    const categorieLabel = (cat: string) => CATEGORIES.find(c => c.val === cat)?.label || cat;

    const tauxColor = (taux: number) => {
        if (taux >= 100) return '#16A34A';
        if (taux >= 80) return '#F59E0B';
        if (taux >= 50) return '#EF4444';
        return '#94A3B8';
    };

    return (
        <div className="suivi-page">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 10, color: '#1E293B' }}>
                        <FiBarChart2 /> Suivi mensuel des ventes
                    </h1>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748B' }}>
                        Objectifs vs ventes réalisées · Style tableur SEDIMA · {rows.length} ligne(s)
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleSync}
                        style={{ padding: '8px 14px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                        <FiRefreshCw size={14} /> Sync activités
                    </button>
                    <button onClick={() => setShowSettings(true)}
                        style={{ padding: '8px 14px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                        <FiSettings size={14} /> Configuration
                    </button>
                </div>
            </div>

            {/* Period + view switcher */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20, padding: 16, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <FiCalendar color="#64748B" />
                    <select value={annee} onChange={e => setAnnee(parseInt(e.target.value))}
                        style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontWeight: 600, fontSize: 14 }}>
                        {yearsOptions.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select value={mois} onChange={e => setMois(parseInt(e.target.value))}
                        style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontWeight: 600, fontSize: 14 }}>
                        {MOIS_LABELS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                    </select>
                </div>

                {calendrier && (
                    <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#64748B', borderLeft: '1px solid #E2E8F0', paddingLeft: 16 }}>
                        <span><strong style={{ color: '#1E293B' }}>{calendrier.jours_ouvrables}</strong> jours ouvrables</span>
                        <span><strong style={{ color: '#1E293B' }}>{calendrier.jours_ouvres}</strong> jours ouvrés</span>
                    </div>
                )}

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, background: '#F1F5F9', padding: 4, borderRadius: 8 }}>
                    {[
                        { k: 'clients', l: 'Clients', i: <FiUsers size={13} /> },
                        { k: 'commerciaux', l: 'Commerciaux', i: <FiTarget size={13} /> },
                        { k: 'zones', l: 'Zones', i: <FiMapPin size={13} /> },
                        { k: 'categories', l: 'Catégories', i: <FiTag size={13} /> }
                    ].map(v => (
                        <button key={v.k} onClick={() => setView(v.k as any)}
                            style={{
                                padding: '6px 12px', border: 'none', borderRadius: 6, cursor: 'pointer',
                                background: view === v.k ? '#fff' : 'transparent',
                                color: view === v.k ? '#16A34A' : '#64748B',
                                fontWeight: 600, fontSize: 13,
                                display: 'flex', alignItems: 'center', gap: 5,
                                boxShadow: view === v.k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                            }}>
                            {v.i} {v.l}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 20 }}>
                <KpiCard icon={<FiTarget />} label="Objectif du mois" value={`${fmt(totals.total_objectif_mois)} T`} color="#3B82F6" />
                <KpiCard icon={<FiPackage />} label="Ventes réalisées" value={`${fmt(totals.total_vente_mois)} T`} color="#16A34A" />
                <KpiCard icon={Number(totals.total_ecart) >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
                    label="Écart"
                    value={`${Number(totals.total_ecart) >= 0 ? '+' : ''}${fmt(totals.total_ecart)} T`}
                    color={Number(totals.total_ecart) >= 0 ? '#16A34A' : '#EF4444'} />
                <KpiCard icon={<FiBarChart2 />} label="Taux global" value={fmtPct(totals.taux_global)} color={tauxColor(Number(totals.taux_global))} />
            </div>

            {/* Filters (only clients view) */}
            {view === 'clients' && (
                <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                        <FiSearch color="#64748B" />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher client ou code..."
                            style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14, background: 'transparent' }} />
                    </div>
                    <div style={{ minWidth: 180 }}>
                        <SearchableSelect value={filterZone} onChange={(v: string) => setFilterZone(v)}
                            placeholder="Toutes zones" icon={<FiMapPin />}
                            options={zones.map((z: any) => ({ value: String(z.id), label: z.nom }))} />
                    </div>
                    <select value={filterCategorie} onChange={e => setFilterCategorie(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, minWidth: 160 }}>
                        <option value="">Toutes catégories</option>
                        {CATEGORIES.map(c => <option key={c.val} value={c.val}>{c.label}</option>)}
                    </select>
                    <div style={{ minWidth: 200 }}>
                        <SearchableSelect value={filterCommercial} onChange={(v: string) => setFilterCommercial(v)}
                            placeholder="Tous commerciaux" icon={<FiUsers />}
                            options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}`, sub: c.zone_nom || '' }))} />
                    </div>
                </div>
            )}

            {/* Table */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
                    {view === 'clients' && (
                        <table className="suivi-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#F8FAFC', position: 'sticky', top: 0 }}>
                                    <Th>Code</Th>
                                    <Th>Client</Th>
                                    <Th>Catégorie</Th>
                                    <Th>Zone</Th>
                                    <Th>Commercial</Th>
                                    <Th align="right">Obj. annuel</Th>
                                    <Th align="right">Obj. {MOIS_LABELS[mois-1]}</Th>
                                    <Th align="right">Vente</Th>
                                    <Th align="right">Écart</Th>
                                    <Th align="right">Taux</Th>
                                    <Th>Source</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>Chargement…</td></tr>}
                                {!loading && rows.length === 0 && <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>Aucun client</td></tr>}
                                {rows.map(r => {
                                    const editingV = editingCell === `v-${r.client_id}`;
                                    const editingO = editingCell === `o-${r.client_id}`;
                                    const taux = Number(r.taux_realisation) || 0;
                                    return (
                                        <tr key={r.client_id} style={{ borderTop: '1px solid #F1F5F9' }}>
                                            <Td><code style={{ fontSize: 11, color: '#64748B' }}>{r.code_client || '—'}</code></Td>
                                            <Td><strong>{r.raison_sociale}</strong></Td>
                                            <Td>
                                                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                                                    background: categorieColor(r.categorie_client) + '22', color: categorieColor(r.categorie_client) }}>
                                                    {categorieLabel(r.categorie_client)}
                                                </span>
                                            </Td>
                                            <Td>{r.zone_nom || '—'}</Td>
                                            <Td>{r.commercial_nom || <span style={{ color: '#EF4444', fontSize: 11 }}>Non affecté</span>}</Td>
                                            <Td align="right">
                                                {editingO ? (
                                                    <InlineEdit value={editValue} onChange={setEditValue}
                                                        onSave={() => handleSaveObjectif(r.client_id, editValue)}
                                                        onCancel={() => setEditingCell('')} />
                                                ) : (
                                                    <span onClick={() => { setEditingCell(`o-${r.client_id}`); setEditValue(String(Math.round(Number(r.obj_janvier||0) + Number(r.obj_fevrier||0) + Number(r.obj_mars||0) + Number(r.obj_avril||0) + Number(r.obj_mai||0) + Number(r.obj_juin||0) + Number(r.obj_juillet||0) + Number(r.obj_aout||0) + Number(r.obj_septembre||0) + Number(r.obj_octobre||0) + Number(r.obj_novembre||0) + Number(r.obj_decembre||0)))); }}
                                                        style={{ cursor: 'pointer', fontWeight: 600, padding: '2px 6px', borderRadius: 4, display: 'inline-block' }}
                                                        title="Cliquez pour modifier l'objectif annuel (la répartition mensuelle s'appliquera automatiquement)">
                                                        {(() => {
                                                            const annuel = ['janvier','fevrier','mars','avril','mai','juin','juillet','aout','septembre','octobre','novembre','decembre']
                                                                .reduce((s, m) => s + Number(r[`obj_${m}`] || 0), 0);
                                                            return annuel > 0 ? fmt(annuel) : '—';
                                                        })()}
                                                    </span>
                                                )}
                                            </Td>
                                            <Td align="right">{fmt(r.objectif_mois)}</Td>
                                            <Td align="right">
                                                {editingV ? (
                                                    <InlineEdit value={editValue} onChange={setEditValue}
                                                        onSave={() => handleSaveVente(r.client_id, editValue)}
                                                        onCancel={() => setEditingCell('')} />
                                                ) : (
                                                    <span onClick={() => { setEditingCell(`v-${r.client_id}`); setEditValue(String(r.vente_mois || 0)); }}
                                                        style={{ cursor: 'pointer', fontWeight: 700, color: '#16A34A', padding: '2px 6px', borderRadius: 4, display: 'inline-block' }}
                                                        title="Cliquez pour modifier la vente">
                                                        {fmt(r.vente_mois)}
                                                    </span>
                                                )}
                                            </Td>
                                            <Td align="right">
                                                <span style={{ color: Number(r.ecart) >= 0 ? '#16A34A' : '#EF4444', fontWeight: 600 }}>
                                                    {Number(r.ecart) >= 0 ? '+' : ''}{fmt(r.ecart)}
                                                </span>
                                            </Td>
                                            <Td align="right">
                                                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                                                    background: tauxColor(taux) + '22', color: tauxColor(taux) }}>
                                                    {fmtPct(taux)}
                                                </span>
                                            </Td>
                                            <Td>
                                                {r.source && (
                                                    <span style={{ fontSize: 10, color: '#64748B' }}>
                                                        {r.source === 'mobile_auto' ? '📱 Auto' : r.source === 'manuelle' ? '✍️ Manuel' : r.source}
                                                        {r.nb_transactions ? ` (${r.nb_transactions})` : ''}
                                                    </span>
                                                )}
                                            </Td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}

                    {view === 'commerciaux' && (
                        <AggTable rows={rows} loading={loading} mois={mois}
                            nameCol={(r: any) => r.commercial_nom}
                            subCol={(r: any) => r.zone_nom} />
                    )}
                    {view === 'zones' && (
                        <AggTable rows={rows} loading={loading} mois={mois}
                            nameCol={(r: any) => r.zone_nom} subCol={() => null} />
                    )}
                    {view === 'categories' && (
                        <AggTable rows={rows} loading={loading} mois={mois}
                            nameCol={(r: any) => categorieLabel(r.categorie)} subCol={() => null} />
                    )}
                </div>
            </div>

            {/* Settings modal */}
            {showSettings && repartition && (
                <SettingsModal
                    annee={annee}
                    repartition={repartition}
                    calendrier={calendrier}
                    onClose={() => setShowSettings(false)}
                    onSaved={() => { setShowSettings(false); load(); }}
                    onApplyAll={handleApplyAll}
                />
            )}
        </div>
    );
};

const Th: React.FC<any> = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 12px', textAlign: align, fontWeight: 700, fontSize: 11,
        color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5,
        borderBottom: '2px solid #E2E8F0', whiteSpace: 'nowrap'
    }}>{children}</th>
);
const Td: React.FC<any> = ({ children, align = 'left' }) => (
    <td style={{ padding: '10px 12px', textAlign: align, color: '#1E293B' }}>{children}</td>
);

const InlineEdit: React.FC<any> = ({ value, onChange, onSave, onCancel }) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <input type="number" value={value} onChange={e => onChange(e.target.value)} autoFocus
            onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
            style={{ width: 80, padding: '3px 6px', border: '1px solid #16A34A', borderRadius: 4, fontSize: 13, textAlign: 'right' }} />
        <button onClick={onSave} style={{ background: '#16A34A', color: '#fff', border: 'none', padding: 4, borderRadius: 4, cursor: 'pointer' }}><FiCheck size={12} /></button>
        <button onClick={onCancel} style={{ background: '#fff', border: '1px solid #E2E8F0', padding: 4, borderRadius: 4, cursor: 'pointer' }}><FiX size={12} /></button>
    </div>
);

const KpiCard: React.FC<any> = ({ icon, label, value, color }) => (
    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 10, background: color + '22', color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{icon}</div>
        <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1E293B', marginTop: 2 }}>{value}</div>
        </div>
    </div>
);

const AggTable: React.FC<any> = ({ rows, loading, mois, nameCol, subCol }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
            <tr style={{ background: '#F8FAFC' }}>
                <Th>Nom</Th>
                <Th align="right">Clients</Th>
                {mois ? <>
                    <Th align="right">Objectif mois</Th>
                    <Th align="right">Vente</Th>
                    <Th align="right">Écart</Th>
                    <Th align="right">Taux</Th>
                </> : <Th align="right">Objectif annuel</Th>}
            </tr>
        </thead>
        <tbody>
            {loading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>Chargement…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>Aucune donnée</td></tr>}
            {rows.map((r: any, i: number) => {
                const name = nameCol(r), sub = subCol(r);
                const taux = Number(r.taux) || 0;
                const tauxC = taux >= 100 ? '#16A34A' : taux >= 80 ? '#F59E0B' : taux >= 50 ? '#EF4444' : '#94A3B8';
                return (
                    <tr key={i} style={{ borderTop: '1px solid #F1F5F9' }}>
                        <Td><strong>{name}</strong>{sub && <div style={{ fontSize: 11, color: '#94A3B8' }}>{sub}</div>}</Td>
                        <Td align="right">{r.nb_clients}</Td>
                        {mois ? <>
                            <Td align="right">{fmt(r.objectif_mois)}</Td>
                            <Td align="right" ><strong style={{ color: '#16A34A' }}>{fmt(r.vente_mois)}</strong></Td>
                            <Td align="right"><span style={{ color: Number(r.ecart) >= 0 ? '#16A34A' : '#EF4444', fontWeight: 600 }}>
                                {Number(r.ecart) >= 0 ? '+' : ''}{fmt(r.ecart)}</span></Td>
                            <Td align="right"><span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: tauxC+'22', color: tauxC }}>{fmtPct(taux)}</span></Td>
                        </> : <Td align="right"><strong>{fmt(r.objectif_annuel)}</strong></Td>}
                    </tr>
                );
            })}
        </tbody>
    </table>
);

const SettingsModal: React.FC<any> = ({ annee, repartition, calendrier, onClose, onSaved, onApplyAll }) => {
    const [rep, setRep] = useState<any>(repartition);
    const [savingRep, setSavingRep] = useState(false);

    const total = ['janvier','fevrier','mars','avril','mai','juin','juillet','aout','septembre','octobre','novembre','decembre']
        .reduce((s, m) => s + Number(rep[`pct_${m}`] || 0), 0);

    const save = async () => {
        if (Math.abs(total - 100) > 0.01) return alert(`Le total doit être 100%, actuellement ${total.toFixed(2)}%`);
        setSavingRep(true);
        try { await updateRepartitionObjectifs(annee, rep); onSaved(); }
        catch (e: any) { alert(e.response?.data?.error || 'Erreur'); }
        finally { setSavingRep(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Configuration {annee}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}><FiX size={20} /></button>
                </div>
                <div style={{ padding: 20 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#1E293B' }}>Répartition saisonnière (%)</h4>
                    <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 12px' }}>Pourcentage de l'objectif annuel allouable à chaque mois. Doit totaliser 100%.</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
                        {MOIS_LABELS.map((m, i) => {
                            const key = `pct_${['janvier','fevrier','mars','avril','mai','juin','juillet','aout','septembre','octobre','novembre','decembre'][i]}`;
                            return (
                                <div key={key}>
                                    <label style={{ fontSize: 10, color: '#64748B', fontWeight: 600 }}>{m}</label>
                                    <input type="number" step="0.5" value={rep[key] || 0}
                                        onChange={e => setRep({ ...rep, [key]: parseFloat(e.target.value) || 0 })}
                                        style={{ width: '100%', padding: '5px 8px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ padding: 10, background: total === 100 ? '#DCFCE7' : '#FEE2E2', borderRadius: 8, fontSize: 13, fontWeight: 600, color: total === 100 ? '#16A34A' : '#EF4444', textAlign: 'center', marginBottom: 16 }}>
                        Total : {total.toFixed(2)}%
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        <button onClick={save} disabled={savingRep || Math.abs(total - 100) > 0.01}
                            style={{ flex: 1, padding: '10px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', opacity: savingRep || Math.abs(total - 100) > 0.01 ? 0.5 : 1 }}>
                            Enregistrer la répartition
                        </button>
                        <button onClick={onApplyAll}
                            style={{ padding: '10px 16px', background: '#fff', color: '#16A34A', border: '1px solid #16A34A', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                            Appliquer à tous les clients
                        </button>
                    </div>

                    {calendrier && (
                        <div style={{ padding: 12, background: '#F8FAFC', borderRadius: 10, fontSize: 13 }}>
                            <strong>Calendrier {MOIS_LABELS[(calendrier.mois || 1) - 1]} {calendrier.annee}</strong>
                            <div style={{ display: 'flex', gap: 14, marginTop: 6, color: '#64748B' }}>
                                <span>{calendrier.jours_ouvrables} jours ouvrables</span>
                                <span>{calendrier.jours_ouvres} jours ouvrés</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SuiviMensuel;
